import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, LoadScriptNext, Polyline, Marker, Circle, Polygon } from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faCompress } from "@fortawesome/free-solid-svg-icons";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAP;

const BASE_CONTAINER_STYLE = { width: "100%", height: "420px", borderRadius: 8 };
const FULLSCREEN_CONTAINER_STYLE = { width: "100%", height: "100%", borderRadius: 0 };
const SINGLE_POINT_ZOOM = 17;
const AERIAL_TILT = 45;
const TILT_DELAY_MS = 800; // let the flat top-down view register before tilting in

// Satellite always on, with the map type control hidden so it can't be switched away from it.
// Google's own fullscreenControl is off too - it puts only the map's own div into the
// browser's native Fullscreen API top-layer, which leaves our popover (a sibling of that
// div, not a descendant) invisible while fullscreen. A custom expand button below
// fullscreens the whole wrapper via plain CSS instead, so everything stays together.
// Tilt is intentionally left out here (defaults to 0/top-down) - it's set imperatively after a
// delay in handleMapLoad instead, because setTilt() on an already-mounted map animates smoothly,
// while setting it as a construction option would just start tilted with no motion.
const MAP_OPTIONS = { mapTypeId: "satellite", mapTypeControl: false, fullscreenControl: false };

// Same red styling Site Geofencing (siteGeofencing.jsx) draws its circle/polygon with -
// kept identical here so a geofence reads as the same thing wherever it's shown.
const GEOFENCE_SHAPE_OPTIONS = {
  fillColor: "#FF0000",
  fillOpacity: 0.35,
  strokeColor: "#FF0000",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  clickable: false,
};

// Compass bearing (degrees, 0-360) from one point to another, so the tilted aerial
// view can be turned to look along the direction of travel rather than sitting at
// whatever heading Google defaults to.
const computeHeading = (from, to) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
};

// Collapses points that share the exact same lat/lng into one marker at that exact
// spot (rather than nudging duplicates apart, which moves the pin off the real GPS
// point) - each group keeps every original point's index/title, in order, so a click
// can list all of them out instead of the pin just showing whichever landed last.
const groupByExactPosition = (points) => {
  const order = [];
  const byKey = new Map();
  points.forEach((p, i) => {
    const key = `${p.lat},${p.lng}`;
    if (!byKey.has(key)) {
      byKey.set(key, { position: p, indices: [] });
      order.push(key);
    }
    byKey.get(key).indices.push(i);
  });
  return order.map((key) => byKey.get(key));
};

// Plots a straight path connecting `points` in order - a plain Polyline (same as how a
// geofence Polygon is drawn elsewhere), no Directions API call and no road-snapping.
// Opens zoomed/panned to fit the whole path and every point in one view, flat top-down,
// then animates into a tilted 45deg aerial view looking along the direction of travel
// (start -> last point) wherever Google has that oblique imagery for the location.
//
// `points` is [{lat, lng}, ...]. `titles` (popover text, e.g. "Clock In - 08:03") is
// optional, same length as `points`. Pins carry no label of their own by default -
// except where two or more points share an exact lat/lng, which renders as one pin
// badged with how many there are; click any pin to see the title(s) for that spot.
// `showLabels` (off by default) letters single (non-overlapping) pins A, B, C, ... in
// point order - meant for the handful-of-stops case (e.g. mobile services' start/finish),
// not a long duty patrol route where that'd just be visual noise.
//
// `geofence` (optional) overlays the site's own geofence - {center: {lat,lng}, radius,
// path} straight off SiteGeoLocation - as a plain shape with no marker/pin of its own,
// same as Site Geofencing draws it: a Circle for a preset radius, or a Polygon for a
// custom-drawn one (radius 0). Pass whichever of radius/path the site actually has;
// the wrong one being empty/undefined is fine, only the shape that applies renders.
export const RouteMap = ({ points = [], titles, showLabels = false, geofence }) => {
  const hasPath = points.length > 1;
  // window.google doesn't exist until the script's onLoad actually fires - nesting
  // under LoadScriptNext only defers whether GoogleMap/Polyline get mounted, not
  // when their JSX props (built during this component's own render) get evaluated,
  // so anything touching window.google.maps must wait on this flag.
  const [mapsReady, setMapsReady] = useState(false);
  const tiltTimeoutRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const groups = useMemo(() => groupByExactPosition(points), [points]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);

  // Google tiles don't reflow on their own when the container's CSS size changes -
  // nudge the map to recompute its viewport once the fullscreen toggle has applied,
  // then restore whatever it was centred/zoomed on (resize resets that otherwise).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.google?.maps?.event) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    const id = setTimeout(() => {
      window.google.maps.event.trigger(map, "resize");
      if (center) map.setCenter(center);
      if (zoom != null) map.setZoom(zoom);
    }, 50);
    return () => clearTimeout(id);
  }, [isFullscreen]);

  // Escape backs out of the custom fullscreen the same way it would the native one.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen]);

  // A Google InfoWindow's own screen-position math fights with this map's tilt/heading
  // (visibly jerky) and its default bubble reads poorly over satellite imagery, so this
  // is a plain, fully-opaque popover positioned from the raw click event instead,
  // completely outside Google's rendering. Opens on click (not hover) since it can list
  // several actions at once - closes on another click, the map moving, or its own X.
  const [popover, setPopover] = useState(null); // {x, y, group} in mapWrapperRef-relative px
  const mapWrapperRef = useRef(null);

  const openPopover = useCallback((group, event) => {
    const wrapper = mapWrapperRef.current;
    if (!wrapper || !event?.domEvent) return;
    const rect = wrapper.getBoundingClientRect();
    setPopover({
      x: event.domEvent.clientX - rect.left,
      y: event.domEvent.clientY - rect.top,
      group,
    });
  }, []);
  const closePopover = useCallback(() => setPopover(null), []);

  const handleMapLoad = useCallback((map) => {
    mapInstanceRef.current = map;
    if (hasPath) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, 48);
    }

    // Open flat/top-down first, then animate into the tilted aerial view - looking
    // along the direction of travel when there's a path - after a short pause.
    tiltTimeoutRef.current = setTimeout(() => {
      if (hasPath) {
        map.setHeading(computeHeading(points[0], points[points.length - 1]));
      }
      map.setTilt(AERIAL_TILT);
    }, TILT_DELAY_MS);
  }, [points, hasPath]);

  // Cancel a pending tilt-in if this unmounts before it fires.
  useEffect(() => () => {
    if (tiltTimeoutRef.current) clearTimeout(tiltTimeoutRef.current);
  }, []);

  if (points.length === 0) {
    return <div style={{ textAlign: "center", color: "#9ca3af" }}>No location data available</div>;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return <div style={{ textAlign: "center", color: "#9ca3af" }}>Map is not configured</div>;
  }

  return (
    <div style={isFullscreen ? { position: "fixed", inset: 0, zIndex: 2000, background: "#000", display: "flex", flexDirection: "column" } : undefined}>
      <div ref={mapWrapperRef} style={isFullscreen ? { position: "relative", flex: 1, minHeight: 0 } : { position: "relative" }}>
      <LoadScriptNext googleMapsApiKey={GOOGLE_MAPS_API_KEY} onLoad={() => setMapsReady(true)}>
        <GoogleMap
          mapContainerStyle={isFullscreen ? FULLSCREEN_CONTAINER_STYLE : BASE_CONTAINER_STYLE}
          center={points[0]}
          zoom={hasPath ? 14 : SINGLE_POINT_ZOOM}
          options={MAP_OPTIONS}
          onLoad={handleMapLoad}
          onClick={closePopover}
          onDragStart={closePopover}
          onZoomChanged={closePopover}
        >
          {geofence?.center && (
            Number(geofence.radius) > 0 ? (
              <Circle center={geofence.center} radius={Number(geofence.radius)} options={GEOFENCE_SHAPE_OPTIONS} />
            ) : geofence.path?.length > 2 ? (
              <Polygon paths={geofence.path} options={GEOFENCE_SHAPE_OPTIONS} />
            ) : null
          )}

          {hasPath && (
            <Polyline
              path={points}
              options={{
                strokeColor: "#0091ae",
                strokeOpacity: 0.9,
                strokeWeight: 3,
                // Arrow at the end so it reads as a direction (start -> finish), not just a
                // shape - only safe to touch window.google.maps once the script has loaded.
                ...(mapsReady && {
                  icons: [
                    {
                      icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3, strokeColor: "#0091ae" },
                      offset: "100%",
                    },
                  ],
                }),
              }}
            />
          )}
          {groups.map((group, gi) => (
            <Marker
              key={gi}
              position={group.position}
              label={
                group.indices.length > 1
                  ? String(group.indices.length)
                  : showLabels
                    ? String.fromCharCode(65 + gi) // A, B, C, ... in point order
                    : undefined
              }
              title={group.indices.length === 1 ? titles?.[group.indices[0]] : undefined}
              onClick={(e) => openPopover(group, e)}
            />
          ))}
        </GoogleMap>
      </LoadScriptNext>

      <button
        type="button"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        style={{
          position: "absolute", top: 8, right: 8, zIndex: 15,
          width: 32, height: 32, border: "none", borderRadius: 6, cursor: "pointer",
          background: "rgba(17,24,39,0.75)", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
      </button>

      {popover && (
        <div
          style={{
            position: "absolute",
            left: popover.x,
            top: popover.y,
            transform: "translate(-50%, -100%)",
            marginTop: -14,
            background: "#111827",
            color: "#fff",
            borderRadius: 6,
            fontSize: 12,
            minWidth: 150,
            maxWidth: 260,
            boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
            zIndex: 20,
          }}
        >
          {popover.group.indices.length > 1 && (
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "6px 10px", borderBottom: "1px solid rgba(255,255,255,0.15)", fontWeight: 600,
            }}>
              <span>{popover.group.indices.length} actions here</span>
              <span onClick={closePopover} style={{ cursor: "pointer", opacity: 0.7, marginLeft: 8 }}>&times;</span>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", maxHeight: 180, overflowY: "auto" }}>
            {popover.group.indices.map((idx, i) => (
              <div
                key={idx}
                style={{
                  padding: "6px 10px",
                  borderTop: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none",
                }}
              >
                {titles?.[idx] || `Point ${idx + 1}`}
              </div>
            ))}
          </div>
          {popover.group.indices.length === 1 && (
            <span
              onClick={closePopover}
              style={{ position: "absolute", top: 4, right: 8, cursor: "pointer", opacity: 0.7 }}
            >
              &times;
            </span>
          )}
        </div>
      )}
      </div>
    </div>
  );
};
